import { tokenChain } from "./caip10";
import type { AdData, AdType } from "./index";

/**
 * How an ad describes itself, and where it points.
 *
 * These live in `@adland/data` rather than in the React package because none of
 * them draw anything. Two things that never import each other need the same
 * answers: the browser, deciding where a tap on the creative goes, and the API,
 * composing a push notification about that same creative. When the destination
 * lived only beside the web adapter, the only way for a server to link to an ad
 * was to reimplement it — and a second copy of "where does a cast go" is a
 * second copy that can disagree with the first.
 */

/**
 * What to call each kind of ad, in the reader's words.
 *
 * Data, not presentation: only the icons that usually accompany these need a
 * component, and a caller that wants the name should not have to take the
 * drawing with it.
 */
export const adTypeLabel: Record<AdType, string> = {
  link: "Link",
  cast: "Cast",
  tweet: "X post",
  miniapp: "Miniapp",
  token: "Token",
  farcasterProfile: "Profile",
};

/**
 * Where an ad goes on the open web, or null if it has nowhere.
 *
 * Three of these were once null — a cast, a token and a profile — on the
 * reasoning that they have no web equivalent. They have exactly the equivalent
 * the Farcaster adapter reaches for; it just reaches through the Mini App SDK.
 * Every one of them is a page on the open web, and returning null made a click
 * on three of the six ad types do nothing at all: no navigation, no error, an ad
 * that looks clickable and is not. An advertiser is paying for those clicks.
 */
export function adDestination(ad: AdData): string | null {
  switch (ad.type) {
    case "link":
    case "miniapp":
    case "tweet":
      return ad.data.url;

    // `/~/conversations/<hash>` rather than `/<username>/<hash-prefix>`, which
    // is the URL people copy: the prefix form needs a username the ad does not
    // store, and the hash form resolves from the hash alone — which is the only
    // thing a cast ad has.
    case "cast":
      return `https://farcaster.xyz/~/conversations/${ad.data.hash}`;

    // By fid, for the same reason: the ad stores the number, and a username can
    // be changed by its owner after the ad is published.
    case "farcasterProfile":
      return `https://farcaster.xyz/~/profiles/${ad.data.fid}`;

    // The nearest thing on the open web to the mini app's `swapToken`: somewhere
    // the token can be seen priced AND bought. Null for a chain DexScreener does
    // not index, which is the one case where "no web equivalent" is really true
    // — better nothing than a link to a page that cannot show the token.
    case "token": {
      const slug = tokenChain(ad.data.chainId)?.dexscreenerSlug;
      return slug ? `https://dexscreener.com/${slug}/${ad.data.address}` : null;
    }

    default:
      return null;
  }
}

/**
 * The one line that names this ad, or null when it has none yet.
 *
 * Written as an explicit switch rather than by sniffing the metadata for a
 * `title`-ish key, which is what the card renderer does. A card can afford to
 * guess: it has room to show several fields and a picture, so landing on the
 * second-best one costs a little polish. This string is going into a push
 * notification with roughly seventy characters to spend, and there it is the
 * whole of what the reader learns about the ad before deciding whether to open
 * it. Each type says what its own headline is.
 *
 * Metadata is optional on every ad — it is resolved after publication and can
 * fail — so every branch tolerates its absence.
 */
export function adHeadline(ad: AdData): string | null {
  const m = ad.metadata as Record<string, unknown> | undefined;
  const str = (v: unknown): string | null =>
    typeof v === "string" && v.trim() ? v.trim() : null;

  switch (ad.type) {
    case "link":
    case "miniapp":
      return str(m?.title);

    // The post itself. A cast and an X post have no title — what they are is
    // what they say — so the text stands in, collapsed to one line because a
    // notification cannot show the line breaks anyway.
    case "cast":
    case "tweet": {
      const text = str(m?.text);
      return text ? text.replace(/\s+/g, " ") : null;
    }

    // The person, by the name they chose, falling back to the handle.
    case "farcasterProfile":
      return str(m?.displayName) ?? str(m?.username) ?? null;

    // Both, because a symbol alone is ambiguous across chains and a name alone
    // is not what anyone searches for.
    case "token": {
      const name = str(m?.name);
      const symbol = str(m?.symbol);
      if (name && symbol) return `${name} (${symbol})`;
      return name ?? symbol;
    }

    default:
      return null;
  }
}

/*
 * There is deliberately no `ellipsize` here.
 *
 * One was written, then removed: `@adland/config` already clamps the headline
 * itself, because it is the only place that knows the notification's remaining
 * character budget, and a second copy exported from here would be one more
 * definition of "cut this to length" for the two to disagree over. Callers that
 * need a shortened headline take it from the template that shortens it.
 */
